import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import type { FormInstance } from "antd";
import { Button, Form, Input, Select, Switch, Spin } from "antd";
import { EditOutlined } from "@ant-design/icons";
import { PageLayout } from "../../layout/PageLayout/PageLayout";
import { User, UserStatus } from "../../models/User";
import { Role } from "../../models/Role";
import { getAllRoles } from "../../services/RoleService";
import { createUser, getUser, updateUser } from "../../services/UserService";
import { useApplicationContext } from "../../application/ApplicationContext";
import "./NewUserPage.scss";
import { Toast } from "primereact/toast";
import CustomButton from "../../components/AccommodationData/CustomButton/CustomButton";

const NewUserPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const { setBreadcrumb } = useApplicationContext();
  const [form] = Form.useForm();
  const [allRoles, setAllRoles] = useState<{ value: string; label: string }[]>(
    []
  );
  const [userActive, setUserActive] = useState<boolean>(true);
  const toast = useRef<any>(null);
  const [activeSpinner, setActiveSpinner] = useState(false);
  const [newUser, setNewUser] = useState<User>({
    nameAndSurname: "",
    role: {} as Role,
    phone: "",
    email: "",
    password: "",
    status: UserStatus.Active,
  });

  useEffect(() => {
    setBreadcrumb(
      id
        ? ["Podešavanja", "Lista korisnika", "Korisnik"]
        : ["Podešavanja", "Lista korisnika", "Novi korisnik"]
    );
    setActiveSpinner(true);
    getAllRoles()
      .then((response) => {
        let allNewRoles: { value: string; label: string }[] = [];
        response.data.result.map((roleItem: any) => {
          allNewRoles.push({ value: roleItem.id, label: roleItem.name });
        });
        setAllRoles(allNewRoles);
      })
      .then(() => {
        if (id !== undefined) {
          getUser(id)
            .then((response) => {
              const data = response.data.result;
              setNewUser({
                nameAndSurname: data.nameAndSurname,
                role: data.role.id,
                phone: data.phone,
                email: data.email,
                password: data.password,
                status:
                  data.status == "Aktivan"
                    ? UserStatus.Active
                    : UserStatus.Blocked,
              });
              form.setFieldValue("nameAndSurname", data.nameAndSurname);
              form.setFieldValue("role", data.role.id);
              form.setFieldValue("phone", data.phone);
              form.setFieldValue("email", data.email);
              form.setFieldValue("password", data.password);
              setUserActive(data.status == "Aktivan");
            })
            .catch((err) => console.log(err));
        }
      })
      .finally(() => setActiveSpinner(false))
      .catch((err) => console.log(err));
  }, []);

  const SubmitButton = ({ form }: { form: FormInstance }) => {
    const [submittable, setSubmittable] = React.useState(false);
    const values = Form.useWatch([], form);
    useEffect(() => {
      form.validateFields({ validateOnly: true }).then(
        () => {
          setSubmittable(true);
        },
        () => {
          setSubmittable(false);
        }
      );
    }, [values]);

    return (
      <CustomButton
        danger={false}
        htmlType={"submit"}
        disabled={!submittable}
        type={"primary"}
        function={() => {
          saveNewUser();
        }}
        text={"Sačuvaj korisnika"}
      />
    );
  };

  const saveNewUser = () => {
    setActiveSpinner(true);
    const formFieldsContent = form.getFieldsValue();
    let copyOfNewUser: User = { ...newUser };
    copyOfNewUser.nameAndSurname = formFieldsContent?.nameAndSurname ?? "";
    copyOfNewUser.email = formFieldsContent?.email ?? "";
    copyOfNewUser.password = formFieldsContent?.password ?? "";
    copyOfNewUser.role = formFieldsContent?.role;
    copyOfNewUser.phone = formFieldsContent?.phone ?? "";
    //@ts-ignore
    if (copyOfNewUser.role == allRoles.find((item) => item.label == 'Host').value) copyOfNewUser.phone = '';
    if (id === undefined) {
      createUser(copyOfNewUser)
        .then(() => {
          toast?.current?.show({
            severity: "success",
            detail: "Uspešno kreiran korisnik",
            life: 3000,
          });
        })
        .finally(() => {
          setActiveSpinner(false);
          navigate("/users", { replace: true });
        })
        .catch((err) => console.log(err));
    } else {
      updateUser(id, copyOfNewUser)
        .then(() => {
          toast?.current?.show({
            severity: "success",
            detail: "Uspešno  ažurirani podaci o korisniku",
            life: 3000,
          });
        })
        .finally(() => {
          setActiveSpinner(false);
          navigate("/users", { replace: true });
        })
        .catch((err) => console.log(err));
    }
  };

  const onActiveSwitchChange = (checked: boolean) => {
    setNewUser((newUser) => ({
      ...newUser,
      ["status"]: checked ? UserStatus.Active : UserStatus.Blocked,
    }));
  };

  return (
    <PageLayout>
      <Toast ref={toast} position={"top-right"} />
      <div className="new-user">
        <Spin spinning={activeSpinner}>
          <div className="new-user-form-body">
            <Form
              form={form}
              name="new-user-form"
              layout="vertical"
              autoComplete="off"
            >
              <Form.Item
                name="nameAndSurname"
                label="Ime i prezime:"
                rules={[
                  { required: true, message: "Potrebno je uneti ime prezime!" },
                ]}
              >
                <Input
                  className="input-label"
                  placeholder="Unesite ime i prezime"
                  addonAfter={<EditOutlined />}
                  disabled={
                    (location.state && !location.state?.edit) !== null
                      ? !location.state?.edit
                      : false
                  }
                />
              </Form.Item>
              <Form.Item
                name="role"
                label="Tip korisnika:"
                rules={[
                  {
                    required: true,
                    message: "Potrebno je izabrati tip korisnika!",
                  },
                ]}
              >
                <Select
                  placeholder="Izaberite tip korisnika"
                  options={allRoles}
                  disabled={
                    (location.state && !location.state?.edit) !== null
                      ? !location.state?.edit
                      : false
                  }
                />
              </Form.Item>
              <Form.Item name="phone" label="Kontakt telefon:">
                <Input
                  placeholder="Unesite kontakt telefon"
                  addonAfter={<EditOutlined />}
                  disabled={
                    (location.state && !location.state?.edit) !== null
                      ? !location.state?.edit
                      : false
                  }
                />
              </Form.Item>
              <Form.Item
                name="email"
                label="E-mail:"
                rules={[
                  {
                    required: true,
                    message: "Potrebno je uneti email korisnika!",
                  },
                  {
                    type: "email",
                    message: "E-mail u neodgovarajućem formatu!",
                  },
                ]}
              >
                <Input
                  placeholder="Unesite e-mail"
                  addonAfter={<EditOutlined />}
                  disabled={
                    (location.state && !location.state?.edit) !== null
                      ? !location.state?.edit
                      : false
                  }
                />
              </Form.Item>
              <Form.Item
                name="password"
                label="Šifra:"
                rules={[
                  { required: true, message: "Potrebno je uneti šifru!" },
                ]}
              >
                <Input.Password
                  placeholder="Unesite šifru"
                  addonAfter={<EditOutlined />}
                  disabled={
                    (location.state && !location.state?.edit) !== null
                      ? !location.state?.edit
                      : false
                  }
                />
              </Form.Item>
              <Form.Item name="switch" className="switch-container">
                <Switch
                  checked={newUser.status == UserStatus.Active}
                  onChange={onActiveSwitchChange}
                  disabled={
                    (location.state && !location.state?.edit) !== null
                      ? !location.state?.edit
                      : false
                  }
                />
                <span className="switch-text">{`${
                  newUser.status === UserStatus.Active ? "Aktivan" : "Blokiran"
                }`}</span>
              </Form.Item>
              <Form.Item></Form.Item>
              <Form.Item></Form.Item>
              <Form.Item className="submit-container">
                <SubmitButton form={form} />
              </Form.Item>
            </Form>
          </div>
        </Spin>
      </div>
    </PageLayout>
  );
};

export default NewUserPage;
